import React, {
  useEffect,
  useState,
  useRef,
  Fragment,
  useCallback,
} from 'react';
import cn from 'classnames';
import * as PropTypes from 'prop-types';
import axios from 'axios';

import endpoints from '../../define/endpoints';
import { Dropdown } from '../common/Dropdown';
import {
  QUERY_PARAMS,
  DROPDOWN_MBL_BEHAVIOUR,
  DROPDOWN_POSITIONS,
} from '../../../constants';
import { checkIsMobileBrowser } from '../../utils/checkIsMobileBrowser';
import debounce from 'lodash.debounce';

import { Icon } from '@iconify/react';
import iconLoading from '@iconify-icons/mdi/loading';
import iconMapMarker from '@iconify-icons/mdi/map-marker';
import iconSadness from '@iconify-icons/mdi/emoticon-sad-outline';
import roundGpsFixed from '@iconify/icons-ic/round-gps-fixed';
import bulbOnSolid from '@iconify/icons-teenyicons/bulb-on-solid';
import iconCollage from '@iconify/icons-mdi/collage';

const SmartSearchTermsAutocomplete = (props) => {
  const ref = useRef(null);
  const defaultSearch = useRef(false);
  const _NEXT_TO_ME = app_main_wpml.close_to_me;
  const _SUGGESTIONS = app_main_wpml.suggestions;
  const _ROME = app_main_wpml.rome;
  const _LAKE_GARDA = app_main_wpml.lake_garda;
  const _VALTELLINA = app_main_wpml.valtellina;
  const _LOMBARDY = app_main_wpml.lombardy;

  const [query, setQuery] = useState(props.selectedSearchTerm ?? '');
  const [oldQuery, setOldQuery] = useState('');
  const [selectedSearchable, setSelectedSearchable] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const nextToMeSearchable = {
    title: _NEXT_TO_ME,
    id: 0,
    type: 'search_terms',
  };
  const defaultSuggestions = [
    { title: _ROME, id: 1, type: 'search_terms', default: 1 },
    { title: _LOMBARDY, id: 2, type: 'search_terms', default: 1 },
    { title: _VALTELLINA, id: 3, type: 'search_terms', default: 1 },
    { title: _LAKE_GARDA, id: 4, type: 'search_terms', default: 1 },
  ];

  const setUserLocation = props.setUserLocation;
  const [isUserLocationEnabled, setIsUserLocationEnabled] = useState(false);

  navigator?.permissions
    ?.query({ name: 'geolocation' })
    .then((res) =>
      setIsUserLocationEnabled(['granted', 'prompt'].includes(res.state))
    );

  useEffect(() => {
    ref.current.value = props.selectedSearchTerm ?? '';
  }, [props.selectedSearchTerm]);

  const handleFocus = () => {
    setIsDropdownOpen(true);

    window.setTimeout(function () {
      ref.current.select();
    }, 1);

    if (props.scrollOnFocus && checkIsMobileBrowser()) {
      window.setTimeout(function () {
        window.scrollTo(
          0,
          ref.current.getBoundingClientRect().top + window.scrollY - 20
        );
      }, 50);
    }
  };

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target) && isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  };

  const loadSuggestions = useCallback(
    debounce(async (query) => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${endpoints.core}/search/${query}`, {
          params: { locale: window.freedomeConfig.locale },
        });

        const mappedResults = Object.entries(response.data).flatMap(
          ([category, items]) => {
            return items.map((item) => ({ ...item, type: category }));
          }
        );

        setSuggestions(mappedResults);
      } catch (error) {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 250),
    []
  );

  const presentSuggestions = () => {
    if (
      query &&
      query.length >= 3 &&
      (query !== oldQuery || defaultSearch.current)
    ) {
      loadSuggestions(query);
    }
  };

  useEffect(presentSuggestions, [query]);

  useEffect(() => {
    document.addEventListener('mouseup', handleClickOutside);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  });

  const handleKeyboard = (e) => {
    switch (e.key) {
      case 'Tab':
      case 'Enter':
        if (suggestions.length > 0) {
          selectSearchable(suggestions[cursor]);
          ref.current.blur();
        }
        break;

      case 'ArrowDown':
        if (suggestions.length > cursor && cursor < suggestions.length - 1) {
          setCursor(cursor + 1);
        }
        break;

      case 'ArrowUp':
        if (cursor > 0) {
          setCursor(cursor - 1);
        }
        break;

      default:
        if (props.scrollOnFocus && checkIsMobileBrowser()) {
          window.scrollTo(
            0,
            ref.current.getBoundingClientRect().top + window.scrollY - 20
          );
        }
    }
  };

  const registerUserPosition = (pos) => {
    localStorage.setItem(
      'userLocation',
      JSON.stringify({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
      })
    );
    setUserLocation(JSON.parse(localStorage.getItem('userLocation')));
    selectSearchable(nextToMeSearchable);
    setIsUserLocationEnabled(true);
  };

  const handleGeolocation = (action) => {
    navigator?.permissions?.query({ name: 'geolocation' }).then((result) => {
      if (result.state === 'granted') {
        selectSearchable(nextToMeSearchable);
        navigator.geolocation.getCurrentPosition((pos) => {
          registerUserPosition(pos);
        });
      } else if (result.state === 'prompt') {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            selectSearchable(nextToMeSearchable);
            registerUserPosition(pos);
            var analytics_data = {
              event: 'navigator',
              allowed: true,
            };
            dataLayer.push(analytics_data);
          },
          (err) => {
            setIsUserLocationEnabled(false);
            var analytics_data = {
              event: 'navigator',
              allowed: false,
            };
            dataLayer.push(analytics_data);
          }
        );
      }
    });
  };

  const selectSearchable = (searchable) => {
    defaultSearch.current =
      searchable.default === undefined || !searchable.default ? false : true;
    setOldQuery(query);
    setQuery(searchable.title);
    setSelectedSearchable(searchable);
    props.onSearchTermSelect(searchable);
    setIsDropdownOpen(false);
    ref.current.value = searchable.title;
  };

  const renderDefaultSuggestions = () => {
    return (
      <Fragment>
        {isUserLocationEnabled && (
          <div
            className="search-terms-autocomplete__suggestion"
            onClick={() => {
              handleGeolocation();
            }}
          >
            <Icon icon={roundGpsFixed} className="icon icon-big" />
            {_NEXT_TO_ME}
          </div>
        )}
        <div className="separator">
          {_SUGGESTIONS}
          <Icon
            icon={bulbOnSolid}
            className="icon color--warning margin-right--1"
            inline
          />
        </div>
        {renderSuggestions(defaultSuggestions)}
      </Fragment>
    );
  };

  const renderSuggestions = (suggestions) => {
    return suggestions.map((suggestion, index) => {
      let _suggestion;
      let classes = cn('search-terms-autocomplete__suggestion', {
        'search-terms-autocomplete__suggestion--highlighted': cursor === index,
      });

      switch (suggestion.type) {
        case 'listings':
          _suggestion = renderListing(suggestion, classes);
          break;
        case 'search_terms':
          _suggestion = renderSearchTerm(suggestion, classes);
          break;
        case 'seo_pages':
          _suggestion = renderSeoPage(suggestion, classes);
          break;
      }

      return _suggestion;
    });
  };

  const renderSearchTerm = (item, classes) => {
    return (
      <div
        key={item.id}
        className={classes}
        onClick={() => selectSearchable(item)}
      >
        <Icon icon={iconMapMarker} className="icon" />
        {item.title}
      </div>
    );
  };

  const renderSeoPage = (item, classes) => {
    return (
      <div
        key={item.id}
        className={classes}
        onClick={() => selectSearchable(item)}
      >
        <Icon icon={iconCollage} className="icon" />
        {item.title}
      </div>
    );
  };

  const renderListing = (item, classes) => {
    let image_link = `${endpoints.bucket}${item.cover_url}?crop=1:1,smart&width=32&resize-filter=bicubic`;
    return (
      <a
        href={item.listing_url}
        key={item.id}
        className={classes}
        onClick={() => selectSearchable(item)}
      >
        <img src={image_link} />
        {item.title}
      </a>
    );
  };

  const renderEmptySuggestions = () => {
    return (
      <div className="search-terms-autocomplete__empty-state">
        <Icon icon={iconSadness} className="icon margin-right--1" inline />
        {app_main_wpml.search_error}
      </div>
    );
  };

  const renderLoader = () => {
    return (
      <div className="search-terms-autocomplete__empty-state">
        <Icon
          icon={iconLoading}
          className="icon icon--spin margin-right--1"
          inline
        />
        {app_main_wpml.loading}...
      </div>
    );
  };

  const renderFunctionRouter = () => {
    if (isLoading) {
      return renderLoader();
    }
    if ((query === '', query.length < 3)) {
      return renderDefaultSuggestions();
    }
    if (suggestions.length === 0) {
      return renderEmptySuggestions();
    }
    return renderSuggestions(suggestions);
  };

  const renderFormInputs = () => {
    let searchablePoi;
    if (selectedSearchable.type === 'seo_pages') {
      searchablePoi = selectedSearchable.poi ?? '';
    } else {
      searchablePoi = selectedSearchable.title;
    }

    return (
      <Fragment>
        <input
          name={QUERY_PARAMS.SEARCH_TERM}
          value={searchablePoi}
          data-gtm-item="search-form"
          data-gtm-detail="search-term"
          hidden
          readOnly
        />
        {selectedSearchable && (selectedSearchable.category_id ?? null) && (
          <input
            hidden
            readOnly
            name={QUERY_PARAMS.CATEGORIES}
            value={selectedSearchable.category_id}
          />
        )}
      </Fragment>
    );
  };

  return (
    <div className="search-terms-autocomplete">
      <input
        ref={ref}
        type="text"
        className="input input--shadowed"
        placeholder={app_main_wpml.search_where_placeholder}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => handleFocus()}
        onKeyDown={(e) => handleKeyboard(e)}
        defaultValue={query}
      />
      {selectedSearchable && renderFormInputs()}
      <Dropdown
        position={DROPDOWN_POSITIONS.LEFT}
        mobileBehaviour={DROPDOWN_MBL_BEHAVIOUR.NONE}
        mobileLabel={app_main_wpml.search_where_placeholder}
        isOpen={isDropdownOpen}
      >
        <div className={'dropdown__content'}>{renderFunctionRouter()}</div>
      </Dropdown>
    </div>
  );
};

SmartSearchTermsAutocomplete.defaultProps = {
  scrollOnFocus: false,
  selectFirstSuggestionOnBlur: true,
};

SmartSearchTermsAutocomplete.propTypes = {
  scrollOnFocus: PropTypes.bool,
  selectFirstSuggestionOnBlur: PropTypes.bool,
  selectedSearchTerm: PropTypes.string,
  onSearchTermSelect: PropTypes.func.isRequired,
};

export default SmartSearchTermsAutocomplete;
